<template>
  <template v-if="!loading && metadataLoaded">
    <refonte-card>
      <refonte-filters-tabs>
        <template v-slot:default>
          <refonte-filters-tabs-item
            label="Commandes confirmées"
            v-bind:selected="filters.commandes === COMMANDES_COMFIRMEES"
            v-bind:on_click="
              () => {
                commandeFilterStore.setFilter(
                  COMMANDES_FILTERS_CONFIGURATION.commandes.name,
                  COMMANDES_COMFIRMEES
                );
              }
            " />
          <refonte-filters-tabs-item
            label="Commandes annulées"
            v-bind:selected="filters.commandes === COMMANDES_ANNULEES"
            v-bind:on_click="
              () => {
                commandeFilterStore.setFilter(
                  COMMANDES_FILTERS_CONFIGURATION.commandes.name,
                  COMMANDES_ANNULEES
                );
              }
            " />
        </template>
      </refonte-filters-tabs>
      <commandes-filters
        v-if="metadataLoaded"
        v-bind:metadatas="metadatas" />
    </refonte-card>
    <refonte-card class="mt-[8px] !gap-0 !p-0">
      <div
        class="flex flex-wrap items-center gap-[8px] px-[12px] pb-[10px] pt-[12px]">
        <refonte-table-number
          v-bind:number="totalElement"
          v-if="totalElement || totalElement == 0"
          v-bind:label="`${totalElement > 1 ? 'Commandes' : 'Commande'}${
            filters.commandes && filters.commandes === COMMANDES_ANNULEES
              ? commandes.length > 1
                ? ' annulées'
                : ' annulée'
              : ''
          }`" />
        <IconSeparatorVertical
          v-if="
            filters.commandes && filters.commandes === COMMANDES_COMFIRMEES
          " />
        <refonte-table-number
          v-if="
            filters.commandes &&
            filters.commandes === COMMANDES_COMFIRMEES &&
            (commandeCount?.nbCommandesARegle ||
              commandeCount?.nbCommandesARegle == 0)
          "
          v-bind:number="commandeCount?.nbCommandesARegle"
          v-bind:label="`${
            commandeCount?.nbCommandesARegle > 1 ? 'Commandes' : 'Commande'
          } à régler`"
          v-bind:on_click="
            () => {
              commandeFilterStore.setFilter(
                COMMANDES_FILTERS_CONFIGURATION.commandeReglee.name,
                false
              );
            }
          " />
      </div>
      <refonte-separator class="px-[12px]" />
      <refonte-card class="!gap-0 !p-0">
        <div class="mt-2 max-h-[70vh] overflow-y-auto">
          <commandes-content
            v-if="!dataLoading"
            v-bind:fetch_data="fetchData" />
          <template v-else>
            <spinner
              sizes="w-20 h-20"
              thickness="border-8" />
          </template>
          <refonte-load-more
            v-bind:show_load="page + 1 != totalPages"
            v-bind:load="load"
            v-bind:total_of_elements="totalElement"
            v-bind:loading="showLoadMore"
            v-bind:number_of_elements="commandes.length" />
        </div>
      </refonte-card>
    </refonte-card>
  </template>
  <template v-else>
    <spinner
      sizes="w-20 h-20"
      thickness="border-8" />
  </template>
</template>

<script setup lang="ts">
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import RefonteSeparator from '@/components/refonte/separators/refonte-separator.vue';
  import { onMounted, ref, watch } from 'vue';
  import Spinner from '@/components/utils/spinner.vue';
  import CommandesContent from '@/views/_espace/_refonte/gestion/_commandes/commandes-content.vue';
  import {
    COMMANDES_ANNULEES,
    COMMANDES_COMFIRMEES,
    COMMANDES_FILTERS_CONFIGURATION,
    useCommandeFilterStore,
    useCommandeStore
  } from '@/stores/commandes';
  import { storeToRefs } from 'pinia';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import RefonteTableNumber from '@/components/refonte/numbers/refonte-table-number.vue';
  import { useRoute, useRouter } from 'vue-router';
  import api_utils from '@/mixin/api_utils';
  import CommandesFilters from '@/views/_espace/_refonte/gestion/_commandes/commandes-filters.vue';
  import RefonteFiltersTabs from '@/components/refonte/filters/refonte-filters-tabs.vue';
  import RefonteFiltersTabsItem from '@/components/refonte/filters/refonte-filters-tabs-item.vue';
  import IconSeparatorVertical from '@/assets/img/refonte/icons/display/icon-separator-vertical.svg?component';
  import filters_utils from '@/mixin/utils/filters_utils';
  import { Filters } from '@/types/gestion/inscriptions-filters-types';
  import {
    CommandeCount,
    CommandeMetadata
  } from '@/types/gestion/commandes-filters-types';
  import { API } from '@/http-common';
  import RefonteLoadMore from '@/components/refonte/inputs/refonte-load-more.vue';

  const loading = ref(false);

  const entrepriseStore = useEntrepriseStore();
  const { activeClientIds } = storeToRefs(entrepriseStore);
  const commandeFilterStore = useCommandeFilterStore();

  const { filters } = storeToRefs(commandeFilterStore);

  const dataLoading = ref<boolean>(false);
  const commandeStore = useCommandeStore();
  const { commandes, page, totalPages, totalElement } =
    storeToRefs(commandeStore);

  const showLoadMore = ref(false);
  const load = async () => {
    showLoadMore.value = true;
    await commandeStore.loadMore(route.query);
    showLoadMore.value = false;
  };

  const fetchData = async () => {
    dataLoading.value = true;
    await fetchCount(route.query);
    await commandeStore.fetchCommandes(route.query);
    dataLoading.value = false;
  };

  const route = useRoute();
  watch(
    () => route.query,
    async () => {
      await initFilters();
    }
  );

  const initFilters = async () => {
    commandeFilterStore.initFiltersFromQuery(route.query);
  };

  onMounted(async () => {
    loading.value = true;
    await initFilters();
    await fetchMetadata();
    loading.value = false;
  });

  const router = useRouter();
  watch(filters, async (newValue, oldValue) => {
    let query = getQuery(filters.value);
    if (activeClientIds.value && activeClientIds.value != '') {
      query = {
        ...query,
        clientIds: activeClientIds.value
      };
    }
    if (filters_utils.methods.isDeepEqual(query, route.query)) {
      await fetchData();
    } else {
      await router.push({ query });
    }
  });
  const getQuery = (filters: Filters) =>
    api_utils.methods.formatParams(filters, [
      COMMANDES_FILTERS_CONFIGURATION.date.name,
      COMMANDES_FILTERS_CONFIGURATION.datePeriodeDebut.name,
      COMMANDES_FILTERS_CONFIGURATION.datePeriodeFin.name,
      COMMANDES_FILTERS_CONFIGURATION.dateFormation.name,
      COMMANDES_FILTERS_CONFIGURATION.dateFormationPeriodeDebut.name,
      COMMANDES_FILTERS_CONFIGURATION.dateFormationPeriodeFin.name
    ]);

  const commandeCount = ref<CommandeCount>();
  const fetchCount = async (query: Filters) => {
    const response = await API.get('client/v2/commandes/count', {
      params: query
    });

    if (response && response.data) {
      commandeCount.value = response.data;
    }
  };
  const metadataLoaded = ref<boolean>(false);
  const metadatas = ref<CommandeMetadata>({
    references: [],
    vendeurs: [],
    formations: [],
    participants: []
  });

  const fetchMetadata = async () => {
    metadataLoaded.value = false;
    const response = await API.get(
      `client/v2/commandes/metadata${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      const data: CommandeMetadata = response.data;
      metadatas.value.references = data.references ?? [];
      metadatas.value.vendeurs = data.vendeurs ?? [];
      metadatas.value.participants = data.participants ?? [];
      metadatas.value.formations = data.formations ?? [];
    }
    metadataLoaded.value = true;
  };
</script>

<style lang="scss" scoped></style>
